/**
 * First-party, cookieless event tracking. The cookies policy promises "no
 * analytics pixels", so this never loads third-party script and never sets
 * cookies/localStorage — each event is a fire-and-forget beacon of an event
 * name plus coarse, non-PII props.
 *
 * Destination comes from VITE_ANALYTICS_ENDPOINT. Unset (current default) the
 * module is a no-op in production and console.debug in dev — call sites stay
 * wired so enabling collection later is a config change, not a code change.
 */

type Props = Record<string, string | number | boolean>;

const endpoint = (import.meta.env.VITE_ANALYTICS_ENDPOINT as string | undefined)?.trim();

export function track(event: string, props?: Props): void {
  if (!endpoint) {
    if (import.meta.env.DEV) {
      console.debug('[analytics]', event, props ?? {});
    }
    return;
  }
  const body = JSON.stringify({
    event,
    props: props ?? {},
    path: window.location.pathname,
    ts: Date.now(),
  });
  try {
    if (!navigator.sendBeacon?.(endpoint, body)) {
      void fetch(endpoint, { method: 'POST', body, keepalive: true }).catch(() => {});
    }
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * Report Core Web Vitals (LCP, CLS, INP + TTFB/FCP) as track() events.
 * Loaded lazily from main.tsx so web-vitals stays out of the critical path.
 */
export async function reportWebVitals(): Promise<void> {
  const { onLCP, onCLS, onINP, onTTFB, onFCP } = await import('web-vitals');
  const send = (metric: { name: string; value: number; rating: string }): void => {
    track('web_vital', {
      metric: metric.name,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      rating: metric.rating,
    });
  };
  onLCP(send);
  onCLS(send);
  onINP(send);
  onTTFB(send);
  onFCP(send);
}
